import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import type { BulkReferencesFilter } from '@/hooks/useBulkCreation';
import { Input, Pill, PillGroup, Select } from '@/components/ui';
import { cn } from '@/lib/utils';

/** Publish-date chip of the reference list (R7). */
export type ReferencePeriod = 'all' | 7 | 30;

/** Sort order of the reference list (R8). */
export type ReferenceSort = 'newest' | 'oldest' | 'name';

export const DEFAULT_REFERENCE_PERIOD: ReferencePeriod = 'all';
export const DEFAULT_REFERENCE_SORT: ReferenceSort = 'newest';

const PERIOD_OPTIONS: { value: ReferencePeriod; labelKey: string }[] = [
  { value: 'all', labelKey: 'seller.bulk.reference.filters.all' },
  { value: 7, labelKey: 'seller.bulk.reference.filters.last7Days' },
  { value: 30, labelKey: 'seller.bulk.reference.filters.last30Days' },
];

const SORT_OPTIONS: { value: ReferenceSort; labelKey: string }[] = [
  { value: 'newest', labelKey: 'seller.bulk.reference.sort.newest' },
  { value: 'oldest', labelKey: 'seller.bulk.reference.sort.oldest' },
  { value: 'name', labelKey: 'seller.bulk.reference.sort.name' },
];

function isReferenceSort(value: string): value is ReferenceSort {
  return SORT_OPTIONS.some((option) => option.value === value);
}

/** The server query for a search term, chip and sort choice. */
export function toReferencesFilter(
  search: string,
  period: ReferencePeriod,
  sort: ReferenceSort
): BulkReferencesFilter {
  const term = search.trim();
  return {
    ...(term ? { search: term } : {}),
    ...(period === 'all' ? {} : { publishedWithinDays: period }),
    sortBy: sort === 'name' ? 'name' : 'publishedAt',
    sortOrder: sort === 'oldest' || sort === 'name' ? 'asc' : 'desc',
  };
}

export interface ReferenceFiltersProps {
  search: string;
  onSearchChange: (search: string) => void;
  period: ReferencePeriod;
  onPeriodChange: (period: ReferencePeriod) => void;
  sort: ReferenceSort;
  onSortChange: (sort: ReferenceSort) => void;
  /** Matching listings; undefined until the first page arrived. */
  total: number | undefined;
  className?: string;
}

/** Search, publish-date chips, sort order and result count above the list. */
export function ReferenceFilters({
  search,
  onSearchChange,
  period,
  onPeriodChange,
  sort,
  onSortChange,
  total,
  className,
}: ReferenceFiltersProps) {
  const { t } = useTranslation();

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <Input
        type="search"
        icon={Search}
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        placeholder={t('seller.bulk.reference.searchPlaceholder')}
        aria-label={t('seller.bulk.reference.searchLabel')}
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PillGroup aria-label={t('seller.bulk.reference.filters.label')}>
          {PERIOD_OPTIONS.map((option) => (
            <Pill
              key={option.value}
              active={period === option.value}
              onClick={() => onPeriodChange(option.value)}
            >
              {t(option.labelKey)}
            </Pill>
          ))}
        </PillGroup>
        <div className="w-full sm:w-52">
          <Select
            value={sort}
            onChange={(event) => {
              if (isReferenceSort(event.target.value)) {
                onSortChange(event.target.value);
              }
            }}
            aria-label={t('seller.bulk.reference.sort.label')}
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.labelKey)}
              </option>
            ))}
          </Select>
        </div>
      </div>
      {total !== undefined && (
        <p className="font-mono text-[12.5px] text-mute" aria-live="polite">
          {t('seller.bulk.reference.resultCount', { count: total })}
        </p>
      )}
    </div>
  );
}
